/* eslint-disable id-length */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import SearchDropdown from '../../../../Components/Dropdown/dropdown';
import Loader from '../../../../Components/Loader';

const STATUS_OPTIONS = [
  { label: 'Approve', name: 'Approve', value: 1, id: 1 },
  { label: 'Reject', name: 'Reject', value: 2, id: 2 },
];

// Helper to resolve string/number value to matching dropdown option object
const getSelectedDropdownValue = (optionsList = [], val) => {
  if (val === null || val === undefined || val === '') return '';
  if (typeof val === 'object') return val;
  const found = optionsList.find(
    (opt) =>
      String(opt.value ?? opt.id ?? opt) === String(val)
      || String(opt.label ?? opt.name ?? opt) === String(val),
  );
  return found || val;
};

const CRStatusUpdateDialog = ({
  visible,
  onHide,
  onSubmit,
  onCancel,
  onFormDataChange,
  rowData,
}) => {
  const [status, setStatus] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      setStatus(null);
      setRemarks('');
    }
  }, [visible]);

  const handleStatusChange = (rawValue) => {
    const val
      = rawValue && typeof rawValue === 'object' && 'value' in rawValue
        ? rawValue.value
        : rawValue;
    setStatus(val);
    onFormDataChange?.({ status: val, remarks });
  };

  const handleRemarksChange = (e) => {
    const val = e.target.value;
    setRemarks(val);
    onFormDataChange?.({ status, remarks: val });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit?.({ status, remarks });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStatus(null);
    setRemarks('');
    onFormDataChange?.({ status: null, remarks: '' });
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  const handleHide = () => {
    reset();
    onHide?.();
  };

  const rnNumberDisplay
    = rowData?.rnNumber
    || rowData?.rn_number
    || rowData?.rnNo
    || rowData?.crn
    || 'RN-451';

  return (
    <Dialog
      header="Update status"
      visible={visible}
      onHide={handleHide}
      className="cr-status-dialog"
      maskClassName="cr-status-dialog-mask"
      modal
      draggable={false}
      resizable={false}
      blockScroll
      dismissableMask={!submitting}
    >
      <div className="cr-status-update-container">
        {submitting && (
          <div className="cr-edit-dialog__submit-overlay" aria-live="polite">
            <Loader text="Updating status…" />
          </div>
        )}

        <form
          className={`cr-status-update-form${submitting ? ' cr-blur' : ''}`}
          onSubmit={handleSubmit}
        >
          <div className="cr-status-update-form-body-wrapper">
            {/* Read-only RN Number Card */}
            <div className="cr-status-update-info-card">
              <span className="cr-status-update-info-label">RN Number</span>
              <span className="cr-status-update-info-value">{rnNumberDisplay}</span>
            </div>

            {/* Status Dropdown */}
            <div className="cr-status-update-field">
              <label htmlFor="cr-status-dropdown" className="cr-status-update-label">
                Status
              </label>
              <SearchDropdown
                options={STATUS_OPTIONS}
                placeholder="Select"
                value={getSelectedDropdownValue(STATUS_OPTIONS, status)}
                onChange={(e) => handleStatusChange(e.target.value)}
                dropdownCss="cr-status-update-search-dropdown"
                searchBox={false}
                disabled={submitting}
              />
            </div>

            {/* Comment Textarea */}
            <div className="cr-status-update-field">
              <label htmlFor="cr-remarks" className="cr-status-update-label">
                Comment<span className="cr-required">*</span>
              </label>
              <textarea
                id="cr-remarks"
                value={remarks}
                onChange={handleRemarksChange}
                placeholder="Enter..."
                rows={3}
                className="cr-remarks-textarea"
                disabled={submitting}
              />
            </div>
          </div>

          <div className="cr-status-update-form-footer">
            <div className="cr-status-update-actions">
              <button
                type="button"
                onClick={handleCancel}
                className="cr-status-btn cr-status-btn--cancel"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="cr-status-btn cr-status-btn--save"
                disabled={status === null || submitting}
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

CRStatusUpdateDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onFormDataChange: PropTypes.func,
  rowData: PropTypes.object,
};

export default CRStatusUpdateDialog;
